import { useState, useEffect, useCallback, useRef } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { Search, X, Plus, Trash2, ShoppingBag, Package, Dumbbell, Tag, ChevronDown, Loader2, CreditCard, ArrowLeft, User, Calendar, ChevronRight, Info, Link2 } from 'lucide-react';
import PaymentLinkModal from './commerce/PaymentLinkModal';
import { useUserContext } from '../contexts/UserContext';
import { useBranch } from '../contexts/BranchContext';
import NumberInput from './ui/NumberInput';
import { GstContext, explainGst, nextDayISO } from '../utils/purchaseGst';
import WeeklySlotPicker from './commerce/WeeklySlotPicker';
import { standingApi, slotText } from './commerce/standingApi';

// ─── Types ─────────────────────────────────────────────────────────────

interface Member {
  id: string;
  fullName: string | null;
  displayName: string | null;
  phoneNumber: string | null;
  email: string | null;
}

interface CatalogItem {
  id: string;
  type: 'membership' | 'service' | 'product';
  name: string;
  price: number;
  gstPercent?: number;
  hsnSac?: string | null;
  description?: string | null;
  durationMonths?: number | null;
  durationDays?: number | null;
  sessionCount?: number | null;
  sku?: string | null;
}

interface LineItem {
  _key: string;
  itemType: 'membership' | 'service' | 'product' | 'other' | 'custom';
  sourceId?: string;
  name: string;
  price: number;
  quantity: number;
  gstPercent: number;      // effective rate charged = gate(branch) ? baseGstPercent : 0
  baseGstPercent: number;  // intrinsic rate (from the item, or a manual override)
  hsnSac?: string | null;
  discount: number; // Absolute discount per unit (calculated if percent)
  discountValue: number; // The user input value
  discountType: 'flat' | 'percent';
  metadata: Record<string, any>;
  /** The offering takes a provider (a trainer plan, PT with a chosen coach): who, at what price. */
  coach?: CoachOptions | null;
}

interface CoachOptions {
  mode: 'member_picks' | 'gym_assigns';
  /** Sessions only with the chosen provider — one must be chosen. */
  locked: boolean;
  basePrice: number;
  coaches: Array<{ id: string; name: string; level: string | null; price: number }>;
  /** Weekly times held with the trainer: fixed (should be chosen) or member's choice (may be). */
  slots: { scheduling: 'fixed_recurring' | 'member_choice'; min: number; max: number; durationMin: number | null } | null;
}

// Prefill rate for a brand-new CUSTOM line (no catalog item to source from).
// Purely a UI convenience — not GST config; staff can change it inline.
const DEFAULT_GST_RATE = 18;

interface SaleBranchOption {
  id: string;
  name: string;
  gstin: string | null;
  chargesGst: boolean;
}

interface CreatePurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  memberId?: string;
  memberName?: string;
  // 'plan' = the "buy a plan" flow: plan-only catalog, one plan, GST from the
  // plan's branch, and a start date constrained to after the active plan ends.
  mode?: 'default' | 'plan';
  // The member's active membership end date (ISO). In plan mode the new plan
  // must start the day after this (or today when there's no active plan).
  activePlanEndDate?: string | null;
}


// ─── Helpers ────────────────────────────────────────────────────────────

function lineTotal(item: LineItem): number {
  const qty = item.quantity;
  const base = item.price * qty;
  let discTotal = 0;
  if (item.discountType === 'flat') {
    discTotal = item.discountValue * qty;
  } else {
    discTotal = (base * item.discountValue) / 100;
  }
  const taxable = base - discTotal;
  return taxable + (taxable * item.gstPercent) / 100;
}

// Mirror of the server's computeTotals (packages/api/src/utils/purchaseTotals.ts):
// the overall discount reduces TAXABLE value — allocated pre-tax across lines by
// net — so the preview total equals exactly what the server charges. (Previously
// this applied the overall discount on the tax-inclusive total and left tax
// unchanged, which overstated a flat-discounted total and got the payment
// rejected by the server's "amount ≤ total" guard.)
function computeSummary(items: LineItem[], overallDiscValue: number, overallDiscType: 'flat' | 'percent') {
  let subtotal = 0, itemDiscountTotal = 0;
  const nets: number[] = [];
  for (const item of items) {
    const qty = item.quantity;
    const base = item.price * qty;
    const disc = item.discountType === 'flat' ? item.discountValue * qty : (base * item.discountValue) / 100;
    subtotal += base;
    itemDiscountTotal += disc;
    nets.push(base - disc);
  }

  const r2 = (n: number) => Math.round(n * 100) / 100;
  const netTotal = nets.reduce((s, n) => s + n, 0);
  const overallDiscountAmount = overallDiscType === 'flat'
    ? (overallDiscValue || 0)
    : (netTotal * (overallDiscValue || 0)) / 100;

  // Per-line round2 (matches the server's splitLineTax) so the preview total
  // equals the charged total to the paise — no full-payment rejection.
  let taxTotal = 0;
  items.forEach((item, i) => {
    const share = netTotal > 0 ? nets[i] / netTotal : 0;
    const taxable = Math.max(0, nets[i] - overallDiscountAmount * share);
    taxTotal += r2((taxable * item.gstPercent) / 100);
  });

  const subtotalR = r2(subtotal), itemDiscR = r2(itemDiscountTotal), overallR = r2(overallDiscountAmount), taxR = r2(taxTotal);
  const taxableValue = Math.max(0, r2(subtotalR - itemDiscR - overallR));
  // Sales resolve to place-of-supply = supplier state (intra-state), so tax
  // splits CGST/SGST. (IGST would apply only to inter-state goods movement.)
  const cgst = r2(taxR / 2);
  const sgst = r2(taxR - cgst);
  return {
    subtotal: subtotalR,
    itemDiscountTotal: itemDiscR,
    taxableValue,
    taxTotal: taxR,
    cgst,
    sgst,
    overallDiscountAmount: overallR,
    total: Math.max(0, r2(subtotalR - itemDiscR - overallR + taxR)),
  };
}

function typeIcon(type: string, className = "w-4 h-4") {
  if (type === 'membership') return <Dumbbell className={`${className} text-blue-500`} />;
  if (type === 'service') return <ShoppingBag className={`${className} text-purple-500`} />;
  if (type === 'product') return <Package className={`${className} text-orange-500`} />;
  return <Tag className={`${className} text-gray-400`} />;
}

const PAYMENT_METHODS = ['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque', 'Other'];
let keyCounter = 0;
function genKey() { return `item-${++keyCounter}-${Date.now()}`; }

// ─── Component ──────────────────────────────────────────────────────────

export default function CreatePurchaseModal({
  isOpen, onClose, onSuccess, memberId: initialMemberId, memberName: initialMemberName,
  mode = 'default', activePlanEndDate = null
}: CreatePurchaseModalProps) {
  const planMode = mode === 'plan';
  // Earliest allowed start date for a plan in plan mode (day after the active
  // plan ends; today when there's none).
  const minPlanStartDate = nextDayISO(activePlanEndDate);
  const { userContext } = useUserContext();
  // The staff user's current branch scope (seeded from their profile default).
  // Used to pre-select the sale branch when it's a valid candidate for this sale.
  const { selectedBranchId: scopeBranchId } = useBranch();
  const [step, setStep] = useState<'member' | 'items' | 'payment'>('member');
  const [submitting, setSubmitting] = useState(false);
  // The total that `paidNow` was last auto-synced to. Lets us tell "pay in
  // full" (paidNow still equals that total) apart from a manually-typed
  // partial amount, so editing line items after visiting the payment step
  // resyncs the former but never silently clobbers the latter.
  const [lastSyncedTotal, setLastSyncedTotal] = useState<number | null>(null);

  const [gymId, setGymId] = useState(() => userContext?.accessibleGyms?.[0] || '');
  useEffect(() => {
    const id = userContext?.accessibleGyms?.[0] || '';
    if (id) setGymId(id);
  }, [userContext]);

  // ── Member
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedMemberName, setSelectedMemberName] = useState('');

  // ── Catalog
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogResults, setCatalogResults] = useState<{ memberships: CatalogItem[]; services: CatalogItem[]; products: CatalogItem[] }>({ memberships: [], services: [], products: [] });
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  // Sale-branch options for this member (their current-plan branches). The
  // selected branch drives the GST rate/toggle applied to the whole cart.
  const [branchOptions, setBranchOptions] = useState<SaleBranchOption[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  // True while we resolve which branch's GST applies. Items can't be added until
  // this settles, so nothing gets added with the wrong tax.
  const [loadingBranchOptions, setLoadingBranchOptions] = useState(false);
  // Whether the member already has an active/planned membership. In the POS flow
  // this hides plans from the catalog (renewals go through the plan flow).
  const [hasActivePlan, setHasActivePlan] = useState(false);
  // Gym-level GST gate, used when the member has no branches (sale is attributed
  // to gym level). Only whether GST is charged — the rate comes from the item.
  const [gymGst, setGymGst] = useState<{ chargesGst: boolean }>({ chargesGst: true });
  // Ref mirror of the gate to avoid a stale closure inside the memoized
  // searchCatalog callback.
  const gstEnabledRef = useRef(true);
  const [overallDiscountValue, setOverallDiscountValue] = useState(0);
  const [overallDiscountType, setOverallDiscountType] = useState<'flat' | 'percent'>('flat');
  const summary = computeSummary(lineItems, overallDiscountValue, overallDiscountType);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  // ── Payment
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paidNow, setPaidNow] = useState('');
  const [dueDate, setDueDate] = useState('');
  // Collect by payment link instead of recording a payment now. Offered only
  // when the gym collects online (Gympe-enabled); otherwise this flow is
  // exactly as before.
  const [linkCaps, setLinkCaps] = useState<{ canCopyLink: boolean; canSendLinkOnWhatsApp: boolean } | null>(null);
  const [collectBy, setCollectBy] = useState<'record' | 'link'>('record');
  // The sale just saved to collect by link: the link modal replaces the POS.
  const [linkForSale, setLinkForSale] = useState<string | null>(null);
  const [activateOnPayment, setActivateOnPayment] = useState(true);
  const [purchaseNotes, setPurchaseNotes] = useState('');
  // The weekly-times picker for one line (offerings with fixed slots).
  const [slotsFor, setSlotsFor] = useState<string | null>(null);
  // Optional buyer GST details for a B2B sale (member buying for a business).
  const [recipientGstin, setRecipientGstin] = useState('');
  const [recipientLegalName, setRecipientLegalName] = useState('');

  // Default paid amount to total when entering the payment step, and keep it
  // in sync with the total if the user goes back to edit line items — as
  // long as they haven't diverged to a manually-typed partial amount (that's
  // their explicit choice and edits to items shouldn't silently overwrite
  // it). A stale amount that now exceeds the (possibly lower) new total is
  // always clamped down, so it can never overpay.
  useEffect(() => {
    if (step !== 'payment') return;
    const total = summary.total;
    const paidNowNum = parseFloat(paidNow) || 0;
    if (lastSyncedTotal === null || paidNowNum === lastSyncedTotal || paidNowNum > total) {
      setPaidNow(total > 0 ? (total % 1 === 0 ? total.toString() : total.toFixed(2)) : '');
      setLastSyncedTotal(total);
    }
  }, [step, summary.total]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      const startStep = initialMemberId ? 'items' : 'member';
      setStep(startStep);
      setSelectedMemberId(initialMemberId || '');
      setSelectedMemberName(initialMemberName || '');
      setLineItems([]);
      setBranchOptions([]);
      setSelectedBranchId(null);
      setLoadingBranchOptions(false);
      setHasActivePlan(false);
      gstEnabledRef.current = true;
      setExpandedItem(null);
      setCatalogSearch('');
      setCatalogResults({ memberships: [], services: [], products: [] });
      setPaymentMethod('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaidNow('');
      setLastSyncedTotal(null);
      setDueDate('');
      setPurchaseNotes('');
      setRecipientGstin('');
      setRecipientLegalName('');
      setMemberSearch('');
      setLinkForSale(null);
      if (!initialMemberId) fetchMembers();
    }
  }, [isOpen, initialMemberId, initialMemberName]);

  // Point the GST gate at a branch (falls back to gym). Rate stays on the item.
  const applyBranchToRefs = (branchId: string | null, branches: SaleBranchOption[], gym: { chargesGst: boolean }) => {
    const b = branchId ? branches.find(x => x.id === branchId) : null;
    gstEnabledRef.current = b ? b.chargesGst : gym.chargesGst;
  };

  // Apply a fetched branch-options payload to state + refs + the cart.
  const applyBranchOptionsData = (data: any) => {
    const branches: SaleBranchOption[] = data?.branches || [];
    const gym = {
      chargesGst: data?.gym?.chargesGst !== false,
    };
    const serverDefaultId: string | null = data?.defaultBranchId ?? null;
    // Prefer the staff user's scope branch, but ONLY when it's a valid candidate
    // for this sale — the sale branch drives GST, so we never force a branch the
    // server didn't offer. Otherwise fall back to the server's recommendation.
    const scopeIsCandidate = !!scopeBranchId && branches.some(b => b.id === scopeBranchId);
    const defaultBranchId: string | null = scopeIsCandidate ? scopeBranchId : serverDefaultId;
    setBranchOptions(branches);
    setGymGst(gym);
    setSelectedBranchId(defaultBranchId);
    applyBranchToRefs(defaultBranchId, branches, gym);
    const b = defaultBranchId ? branches.find(x => x.id === defaultBranchId) : null;
    const enabled = b ? b.chargesGst : gym.chargesGst;
    // Gate only: keep each line's intrinsic rate, zero it when GST is off.
    setLineItems(prev => prev.map(li => ({ ...li, gstPercent: enabled ? (li.baseGstPercent ?? 0) : 0 })));
  };

  // Load the member's sale-branch options + recommended default so the POS flow
  // can charge the right branch's GST and let staff switch branches. Skipped in
  // plan mode, where GST comes from the *plan's* branch instead (loaded on add).
  // Non-critical: on any failure we silently keep the current defaults.
  useEffect(() => {
    if (!isOpen || !gymId || !selectedMemberId || planMode) return;
    let cancelled = false;
    setLoadingBranchOptions(true);
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch(
          `${API_BASE_URL}/api/gym-purchases/sale-branch-options?gymId=${encodeURIComponent(gymId)}&memberId=${encodeURIComponent(selectedMemberId)}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (!cancelled && res.ok) {
          const data = (await res.json())?.data;
          setHasActivePlan(!!data?.hasActivePlan);
          applyBranchOptionsData(data);
        }
      } catch { /* keep existing default */ }
      finally { if (!cancelled) setLoadingBranchOptions(false); }
    })();
    return () => { cancelled = true; };
  }, [isOpen, gymId, selectedMemberId, planMode]);

  // Plan mode: resolve GST from the branch(es) of the plan just added.
  const loadPlanBranchOptions = async (planId: string) => {
    if (!gymId) return;
    setLoadingBranchOptions(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(
        `${API_BASE_URL}/api/gym-purchases/plan-sale-branch-options?gymId=${encodeURIComponent(gymId)}&planId=${encodeURIComponent(planId)}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (res.ok) applyBranchOptionsData((await res.json())?.data);
    } catch { /* keep existing default */ }
    finally { setLoadingBranchOptions(false); }
  };

  // Staff switches the sale branch: flip the GST gate only. Each line keeps its
  // own rate (so mixed-rate carts survive); rates zero out on a Bill-of-Supply
  // branch and restore from baseGstPercent when a registered branch is picked.
  const handleBranchChange = (branchId: string | null) => {
    setSelectedBranchId(branchId);
    applyBranchToRefs(branchId, branchOptions, gymGst);
    const enabled = gstEnabledRef.current;
    setLineItems(prev => prev.map(li => ({ ...li, gstPercent: enabled ? (li.baseGstPercent ?? 0) : 0 })));
  };

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/members?limit=200`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (res.ok) setMembers((await res.json()).data || []);
    } catch { toast.error('Failed to load members'); }
    finally { setLoadingMembers(false); }
  };

  // Latest catalog request wins, so a slow earlier response (e.g. the initial
  // full load) can't overwrite results for what the user has since typed.
  const catalogReqRef = useRef(0);
  const searchCatalog = useCallback(async (q: string) => {
    const reqId = ++catalogReqRef.current;
    setLoadingCatalog(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/catalog/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (reqId !== catalogReqRef.current) return;
      if (res.ok) {
        const raw = (await res.json()).data as { memberships: any[]; services: any[]; products: any[] };
        // Intrinsic rate = the item's OWN rate (incl. a deliberate 0%), else the
        // standard-rate fallback. Gate-independent — the branch gate is applied
        // when the item is added to the cart, not here, so switching branches
        // later preserves each item's rate.
        const rate = (v: any) => (v === null || v === undefined || v === '' ? DEFAULT_GST_RATE : Number(v));
        setCatalogResults({
          memberships: raw.memberships.map(m => ({ id: m.id, type: 'membership' as const, name: m.name, price: Number(m.price) || 0, gstPercent: rate(m.gstPercent), hsnSac: m.hsnSac ?? null, description: m.description, durationMonths: m.durationMonths })),
          services: raw.services.map(s => ({ id: s.id, type: 'service' as const, name: s.name, price: Number(s.defaultPrice) || 0, gstPercent: rate(s.gstPercent), hsnSac: s.hsnSac ?? null, description: s.description, durationDays: s.durationDays, sessionCount: s.sessionCount })),
          products: raw.products.map(p => ({ id: p.id, type: 'product' as const, name: p.name, price: Number(p.price) || 0, gstPercent: rate(p.gstPercent), hsnSac: p.hsnSac ?? null, sku: p.sku }))
        });
      }
    } catch { }
    finally { if (reqId === catalogReqRef.current) setLoadingCatalog(false); }
  }, []);

  // Load the full catalog as soon as the items step opens, rather than
  // waiting for the search box to be focused. The box is cleared too, so it
  // always matches the list shown (e.g. coming back from the payment step).
  useEffect(() => {
    if (!isOpen || step !== 'items') return;
    setCatalogSearch('');
    searchCatalog('');
  }, [isOpen, step, searchCatalog]);

  const addCatalogItem = (item: CatalogItem) => {
    if (loadingBranchOptions) return; // wait until GST criteria are resolved
    if (item.type === 'membership') {
      if (lineItems.some(i => i.itemType === 'membership')) {
        toast.error('Only one membership plan allowed per purchase.');
        return;
      }
    } else {
      // Adding the same catalog item again just bumps its quantity.
      const existing = lineItems.find(i => i.sourceId === item.id && i.itemType === item.type);
      if (existing) {
        setLineItems(prev => prev.map(i => i._key === existing._key ? { ...i, quantity: i.quantity + 1 } : i));
        setExpandedItem(existing._key);
        setCatalogSearch('');
        return;
      }
    }
    const key = genKey();
    const today = new Date().toISOString().split('T')[0];
    // In plan mode a plan must start after the current plan ends.
    const membershipStart = planMode ? minPlanStartDate : today;
    setLineItems(prev => [...prev, {
      _key: key, itemType: item.type, sourceId: item.id, name: item.name,
      price: item.price, quantity: 1,
      baseGstPercent: item.gstPercent || 0,
      gstPercent: gstEnabledRef.current ? (item.gstPercent || 0) : 0,
      hsnSac: item.hsnSac ?? null,
      discount: 0, discountValue: 0, discountType: 'flat',
      metadata: item.type === 'membership'
        ? { startDate: membershipStart, endDate: '', durationMonths: item.durationMonths }
        : item.type === 'service' ? { durationDays: item.durationDays, sessionCount: item.sessionCount }
        : {}
    }]);
    setExpandedItem(key);
    setCatalogSearch('');
    if (item.type !== 'product') loadCoachOptions(key, item.type === 'membership' ? 'plan' : 'service', item.id);
    // Plan mode: GST follows the plan's branch, resolved now.
    if (planMode && item.type === 'membership') loadPlanBranchOptions(item.id);
  };

  // Offerings with a provider step get a trainer picker; the price follows the
  // provider's level or own price (the same resolution the member store uses).
  const loadCoachOptions = async (key: string, ownerType: 'plan' | 'service', ownerId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/staff/coach-options?ownerType=${ownerType}&ownerId=${ownerId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const json = await res.json();
      if (res.ok && json.success && json.data) {
        setLineItems(prev => prev.map(i => (i._key === key ? { ...i, coach: json.data } : i)));
      }
    } catch { /* no picker — the sale still works as before */ }
  };

  const chooseCoach = (key: string, coachId: string) =>
    setLineItems(prev => prev.map(i => {
      if (i._key !== key || !i.coach) return i;
      const c = i.coach.coaches.find(x => x.id === coachId);
      // A new trainer means new weekly times: the old ones were checked against someone else.
      const { trainerId: _t, trainerName: _n, standing: _s, ...rest } = i.metadata;
      return c
        ? { ...i, price: c.price, metadata: { ...rest, trainerId: c.id, trainerName: c.name } }
        : { ...i, price: i.coach.basePrice, metadata: rest };
    }));

  const addCustomItem = () => {
    const key = genKey();
    setLineItems(prev => [...prev, {
      _key: key, itemType: 'other', name: '', price: 0, quantity: 1,
      baseGstPercent: DEFAULT_GST_RATE,
      gstPercent: gstEnabledRef.current ? DEFAULT_GST_RATE : 0,
      discount: 0, discountValue: 0, discountType: 'flat', metadata: {}
    }]);
    setExpandedItem(key);
  };

  // Editing the GST% inline sets the line's INTRINSIC rate (baseGstPercent) too,
  // so a manual override survives a later branch switch (which re-applies the gate).
  const updateItem = (key: string, field: keyof LineItem, value: any) =>
    setLineItems(prev => prev.map(i => i._key === key
      ? { ...i, [field]: value, ...(field === 'gstPercent' ? { baseGstPercent: value } : {}) }
      : i));
  const updateItemMeta = (key: string, f: string, v: any) =>
    setLineItems(prev => prev.map(i => i._key === key ? { ...i, metadata: { ...i.metadata, [f]: v } } : i));
  const removeItem = (key: string) => {
    setLineItems(prev => prev.filter(i => i._key !== key));
    if (expandedItem === key) setExpandedItem(null);
  };

  // Collecting by link: nothing is received now, whatever the amount field says.
  const paidNowNum = collectBy === 'link' && linkCaps?.canCopyLink ? 0 : parseFloat(paidNow) || 0;
  const pendingNum = Math.max(0, (summary?.total || 0) - paidNowNum);

  // Effective GST context for the current sale branch (falls back to gym level).
  // Drives the per-line "why this GST" explanation.
  const effectiveBranch = selectedBranchId ? branchOptions.find(b => b.id === selectedBranchId) : null;
  const gstContext: GstContext = {
    enabled: effectiveBranch ? effectiveBranch.chargesGst : gymGst.chargesGst,
    sourceLabel: effectiveBranch ? effectiveBranch.name : 'the gym',
  };

  useEffect(() => {
    if (step !== 'payment' || !gymId || linkCaps) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${API_BASE_URL}/api/commerce/capabilities?gymId=${encodeURIComponent(gymId)}`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        const json = await res.json();
        if (res.ok && json.success) setLinkCaps(json.data);
      } catch {
        /* no link option — the record-payment flow is unaffected */
      }
    })();
  }, [step, gymId, linkCaps]);

  const byLink = collectBy === 'link' && !!linkCaps?.canCopyLink;

  const handleSubmit = async () => {
    if (!selectedMemberId) return toast.error('No member selected');
    if (!gymId) return toast.error('Gym context not loaded yet');
    if (planMode && !lineItems.some(i => i.itemType === 'membership')) return toast.error('Select a plan to continue');
    if (lineItems.length === 0) return toast.error('Add at least one item');
    for (const item of lineItems) {
      if (item.itemType === 'membership' && !item.metadata?.startDate) return toast.error(`Set a start date for "${item.name}"`);
      if (item.itemType === 'membership' && planMode && item.metadata?.startDate < minPlanStartDate) {
        return toast.error(`Plan must start on or after ${new Date(minPlanStartDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`);
      }
      if (item.itemType === 'custom' && !item.name.trim()) return toast.error('Custom item name cannot be empty');
      if (item.coach?.locked && !item.metadata?.trainerId) return toast.error(`Choose a trainer for "${item.name}"`);
    }
    if (!byLink && !paymentMethod && paidNowNum > 0) return toast.error('Select a payment method');

    // Optional buyer GSTIN (B2B). Only relevant on a Tax Invoice; validate shape
    // and require a company name when present (server re-validates). GSTIN =
    // 2-digit state + 10-char PAN + entity code + 'Z' + checksum (15 total).
    const buyerGstin = gstContext.enabled ? recipientGstin.trim().toUpperCase() : '';
    if (buyerGstin) {
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(buyerGstin)) {
        return toast.error('Enter a valid 15-character buyer GSTIN, or leave it blank');
      }
      if (!recipientLegalName.trim()) {
        return toast.error('Enter the buyer’s company / legal name for the GSTIN');
      }
    }

    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/gym-purchases`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberId: selectedMemberId,
          gymId,
          branchId: selectedBranchId,
          lineItems: lineItems.map(i => ({
            itemType: i.itemType, 
            sourceId: i.sourceId || null, 
            name: i.name, 
            price: i.price, 
            quantity: i.quantity,
            gstPercent: i.gstPercent,
            hsnSac: i.hsnSac || null,
            discountType: i.discountType,
            discountValue: i.discountValue,
            metadata: i.metadata
          })),
          overallDiscountValue,
          overallDiscountType,
          payment: !byLink && paymentMethod ? { amount: paidNowNum, paymentMethod, paymentDate, notes: '' } : null,
          dueDate: dueDate || null,
          ...(byLink && activateOnPayment && { activationPolicy: 'on_payment' }),
          notes: purchaseNotes.trim() || null,
          recipientGstin: buyerGstin || null,
          recipientLegalName: buyerGstin ? recipientLegalName.trim() : null
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to create purchase');
      if (byLink) {
        // The sale is saved; the link modal generates the link to copy or send
        // on WhatsApp. If that fails, staff can reopen it from the sale
        // (Purchases → the sale → Collect online). The parent hears of the sale
        // when the modal closes — some parents unmount this on success.
        toast.success('Sale saved');
        setLinkForSale(data.data.id);
        return;
      }
      toast.success('Purchase finalized successfully!');
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create purchase');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (linkForSale && gymId && selectedMemberId) {
    return (
      <PaymentLinkModal
        gymId={gymId}
        memberId={selectedMemberId}
        purchaseId={linkForSale}
        memberName={selectedMemberName}
        onClose={() => { setLinkForSale(null); onSuccess(); onClose(); }}
      />
    );
  }

  const filteredMembers = members.filter(m => {
    if (!memberSearch.trim()) return true;
    const q = memberSearch.toLowerCase();
    return (m.fullName || '').toLowerCase().includes(q)
      || (m.displayName || '').toLowerCase().includes(q)
      || (m.phoneNumber || '').includes(q);
  });

  const visibleSteps: Array<'member' | 'items' | 'payment'> = initialMemberId ? ['items', 'payment'] : ['member', 'items', 'payment'];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1001]" {...backdropClose(onClose)}>
      <div
        className="bg-white shadow-2xl w-full h-full md:h-[90vh] md:max-w-6xl md:rounded-2xl flex flex-col overflow-hidden transition-all duration-300"
        onClick={e => e.stopPropagation()}
      >
        {slotsFor && (() => {
          const line = lineItems.find(l => l._key === slotsFor);
          if (!line?.sourceId || !line.metadata?.trainerId) return null;
          const ownerType = line.itemType === 'membership' ? 'plan' : 'service';
          const qs = new URLSearchParams({ ownerType, ownerId: line.sourceId, trainerId: line.metadata.trainerId, memberId: selectedMemberId });
          if (line.metadata.startDate) qs.set('startDate', line.metadata.startDate);
          return (
            <WeeklySlotPicker
              title={`Weekly times — ${line.name}`}
              load={branchId => { if (branchId) qs.set('branchId', branchId); return standingApi(`/offering-grid?${qs.toString()}`); }}
              initial={line.metadata.standing?.times}
              onClose={() => setSlotsFor(null)}
              onSave={pick => { updateItemMeta(line._key, 'standing', pick); setSlotsFor(null); }}
            />
          );
        })()}
        {/* Simple Header */}
        <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            {((step === 'payment') || (step === 'items' && !initialMemberId)) && (
              <button
                onClick={() => {
                  if (step === 'payment') setStep('items');
                  else setStep('member');
                }}
                className="p-2 -ml-2 rounded-lg hover:bg-slate-50 transition-colors"
                title="Go Back"
              >
                <ArrowLeft className="w-5 h-5 text-slate-500" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {step === 'member' ? 'Select Member' : step === 'items' ? (planMode ? 'Buy Future Plan' : 'Select Items') : 'Payment Information'}
              </h2>
              {selectedMemberName && (
                <p className="text-xs font-medium text-slate-500 flex items-center gap-1 mt-0.5">
                  <User className="w-3 h-3" /> {selectedMemberName}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-6">
             <div className="hidden md:flex gap-4">
                {visibleSteps.map((s, i) => {
                  const isActive = step === s;
                  return (
                    <div key={s} className="flex items-center gap-2">
                       <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${isActive ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                         {i + 1}
                       </div>
                       <span className={`text-xs font-semibold ${isActive ? 'text-slate-800' : 'text-slate-400'}`}>
                         {s.charAt(0).toUpperCase() + s.slice(1)}
                       </span>
                       {i < visibleSteps.length - 1 && <div className="w-8 h-px bg-slate-200 ml-2" />}
                    </div>
                  );
                })}
             </div>
             <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 transition-colors">
               <X className="w-5 h-5 text-slate-400" />
             </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* STEP 1: Member Selection */}
          {step === 'member' && (
            <div className="flex-1 flex flex-col items-center p-6 md:p-12 overflow-y-auto">
              <div className="w-full max-w-xl space-y-6">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text" placeholder="Search member by name or mobile..." value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 outline-none transition-all text-base"
                    autoFocus
                  />
                </div>

                <div className="grid grid-cols-1 gap-2">
                  {loadingMembers ? (
                    <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-300" /></div>
                  ) : filteredMembers.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-2xl">
                      <p className="text-sm font-medium text-slate-400">No members found matching "{memberSearch}"</p>
                    </div>
                  ) : (
                    filteredMembers.map(member => (
                      <button key={member.id} onClick={() => { setSelectedMemberId(member.id); setSelectedMemberName(member.displayName || member.fullName || ''); setStep('items'); }}
                        className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/30 transition-all group text-left">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-bold">
                            {(member.displayName || member.fullName || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{member.displayName || member.fullName || 'Unnamed'}</p>
                            <p className="text-xs text-slate-500">{member.phoneNumber || 'No phone'}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Items Selection */}
          {step === 'items' && (
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {/* Sleek Catalog */}
              <div className={`w-full md:w-80 border-b md:border-b-0 md:border-r border-slate-100 flex flex-col shrink-0 transition-opacity ${loadingBranchOptions ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="p-4 border-b border-slate-100 space-y-3 bg-slate-50/50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text" placeholder="Search catalog..." value={catalogSearch}
                      onChange={e => { setCatalogSearch(e.target.value); searchCatalog(e.target.value); }}
                      className="w-full pl-9 pr-8 py-2 rounded-lg border border-slate-200 text-sm focus:border-indigo-500 outline-none bg-white shadow-sm"
                    />
                    {loadingCatalog && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-indigo-500" />}
                  </div>
                  {!planMode && (
                    <button onClick={addCustomItem} disabled={loadingBranchOptions} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 hover:bg-indigo-600 hover:text-white transition-all text-xs font-black uppercase tracking-widest shadow-sm group disabled:opacity-50 disabled:cursor-not-allowed">
                      <Plus className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                      Add Custom Item
                    </button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-4">
                  {loadingCatalog ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                      <p className="text-xs font-bold uppercase tracking-widest">Loading catalog…</p>
                    </div>
                  ) : (
                    <>
                      {/* Membership Plans — hidden in POS once the member has an
                          active plan (renewals go through the plan flow). */}
                      {!lineItems.some(i => i.itemType === 'membership') && (planMode || !hasActivePlan) && (
                        <div className="space-y-1">
                          <p className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Plans</p>
                          {catalogResults.memberships.map(item => (
                            <CatalogMiniItem key={item.id} item={item} onAdd={() => addCatalogItem(item)} />
                          ))}
                        </div>
                      )}
                      {/* Services & products — plan flow is plan-only. */}
                      {!planMode && (
                        <div className="space-y-1">
                          <p className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Services & Products</p>
                          {[...catalogResults.services, ...catalogResults.products].map(item => (
                            <CatalogMiniItem key={item.id} item={item} onAdd={() => addCatalogItem(item)} />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Main List */}
              <div className="flex-1 flex flex-col overflow-hidden bg-slate-50/30">
                {loadingBranchOptions ? (
                  <div className="px-4 md:px-6 pt-4 flex items-center gap-2.5 text-indigo-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm font-semibold">Checking GST criteria</span>
                    <LoadingDots />
                  </div>
                ) : (
                  <div className="px-4 md:px-6 pt-4 flex flex-wrap items-center gap-3">
                    {branchOptions.length > 1 && (
                      <>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Sale Branch</label>
                        <div className="relative">
                          <select
                            value={selectedBranchId ?? ''}
                            onChange={e => handleBranchChange(e.target.value || null)}
                            className="appearance-none pl-3 pr-9 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 bg-white shadow-sm focus:border-indigo-500 outline-none"
                          >
                            {branchOptions.map(b => (
                              <option key={b.id} value={b.id}>
                                {b.name}{b.chargesGst ? ' — Tax Invoice' : ' — No GST'}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                      </>
                    )}
                    {/* Document-type gate — dominant, so staff know whether GST is charged */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-black uppercase tracking-wide border ${gstContext.enabled ? 'bg-teal-50 text-teal-700 border-teal-100' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                      {gstContext.enabled ? 'Tax Invoice' : 'Bill of Supply · No GST'}
                      {gstContext.enabled && effectiveBranch?.gstin && (
                        <span className="font-semibold normal-case tracking-normal text-teal-600/80">· {effectiveBranch.gstin}</span>
                      )}
                    </span>
                  </div>
                )}
                <div className="flex-1 overflow-y-auto px-2 py-4 md:px-3 md:py-5 pb-32">
                  {lineItems.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center opacity-40 py-20">
                      <ShoppingBag className="w-12 h-12 mb-4" />
                      <p className="text-sm font-medium">Select items from the catalog to add to purchase</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold text-[11px] uppercase tracking-wide">
                            <th className="text-left py-2 px-3">Item</th>
                            <th className="text-center py-2 px-1 w-16 border-l border-slate-100">Qty</th>
                            <th className="text-center py-2 px-1 w-20 border-l border-slate-100">GST%</th>
                            <th className="text-center py-2 px-1 w-32 border-l border-slate-100">Discount</th>
                            <th className="text-right py-2 px-3 w-24 border-l border-slate-100">Price (₹)</th>
                            <th className="text-right py-2 px-3 w-24 border-l border-slate-100">Total (₹)</th>
                            <th className="w-8 border-l border-slate-100"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {lineItems.map(item => (
                            <EditableRow
                              key={item._key}
                              item={item}
                              gstContext={gstContext}
                              minStartDate={planMode ? minPlanStartDate : undefined}
                              onUpdate={(f, v) => updateItem(item._key, f, v)}
                              onUpdateMeta={(f, v) => updateItemMeta(item._key, f, v)}
                              onChooseCoach={id => chooseCoach(item._key, id)}
                              onPickSlots={() => setSlotsFor(item._key)}
                              onRemove={() => removeItem(item._key)}
                            />
                          ))}
                        </tbody>
                      </table>
                      {/* mobile list is handled inside EditableRow by rendering differently */}
                    </div>
                  )}
                </div>

                {/* Simplified Summary */}
                {lineItems.length > 0 && (
                  <div className="border-t border-slate-200 bg-white p-6 shrink-0 shadow-lg">
                    <div className="max-w-sm ml-auto space-y-3">
                       <div className="flex justify-between text-xs text-slate-500">
                         <span>Subtotal</span>
                         <span className="font-semibold text-slate-700">₹{summary.subtotal.toLocaleString()}</span>
                       </div>
                       
                       {summary.itemDiscountTotal > 0 && (
                        <div className="flex justify-between text-xs text-emerald-600">
                          <span>Item Discounts</span>
                          <span className="font-semibold">−₹{summary.itemDiscountTotal.toLocaleString()}</span>
                        </div>
                       )}

                       <div className="flex justify-between text-xs text-slate-500">
                         <span>Tax (GST)</span>
                         <span className="font-semibold text-slate-700">₹{summary.taxTotal.toLocaleString()}</span>
                       </div>

                       <div className="pt-2 border-t border-slate-50 space-y-2">
                         <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span className="text-indigo-600 font-bold uppercase tracking-tighter">Overall Discount</span>
                              <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                                <button onClick={() => setOverallDiscountType('flat')} className={`px-2 py-0.5 rounded text-[9px] font-black tracking-tight transition-all ${overallDiscountType === 'flat' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>FLAT</button>
                                <button onClick={() => setOverallDiscountType('percent')} className={`px-2 py-0.5 rounded text-[9px] font-black tracking-tight transition-all ${overallDiscountType === 'percent' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>%</button>
                              </div>
                            </div>
                            <NumberInput min={0} emptyValue={0} value={overallDiscountValue || undefined} onChange={v => setOverallDiscountValue(v ?? 0)} placeholder="0" className="w-16 text-right font-bold text-indigo-600 outline-none p-0 focus:ring-0 appearance-none bg-transparent" />
                         </div>
                         {summary.overallDiscountAmount > 0 && (
                           <div className="flex justify-between text-[11px] text-indigo-600/70 font-medium pl-4">
                              <span>Applied Discount</span>
                              <span>−₹{summary.overallDiscountAmount.toLocaleString()}</span>
                           </div>
                         )}
                       </div>

                       <div className="flex justify-between items-center pt-3 mt-2 border-t border-slate-100">
                         <span className="text-sm font-bold text-slate-800">Total Payable</span>
                         <span className="text-2xl font-black text-slate-900 tracking-tight">₹{summary.total.toLocaleString()}</span>
                       </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Payment */}
          {step === 'payment' && (
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-slate-50/50">
               <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-8 bg-white md:border-r border-slate-100">
                  <div className="max-w-2xl mx-auto space-y-8">
                    {linkCaps?.canCopyLink && (
                      <section className="grid grid-cols-2 gap-3">
                        {([
                          ['record', 'Record payment now', 'Cash, UPI, card — at the desk'],
                          ['link', 'Send payment link', linkCaps.canSendLinkOnWhatsApp ? 'Member pays online via WhatsApp link' : 'Member pays online — copy & share the link'],
                        ] as const).map(([k, title, sub]) => (
                          <button key={k} onClick={() => setCollectBy(k)}
                            className={`text-left p-4 rounded-xl border-2 transition-all ${collectBy === k ? 'border-indigo-600 bg-indigo-50' : 'border-slate-100 bg-white hover:border-slate-200'}`}>
                            <p className="text-sm font-bold text-slate-800 flex items-center gap-2">
                              {k === 'link' ? <Link2 className="w-4 h-4 text-indigo-500" /> : <CreditCard className="w-4 h-4 text-indigo-500" />}{title}
                            </p>
                            <p className="text-[11px] text-slate-500 mt-1">{sub}</p>
                          </button>
                        ))}
                      </section>
                    )}

                    {byLink && (
                      <section className="p-5 bg-indigo-50/60 border border-indigo-100 rounded-2xl space-y-4">
                        <p className="text-sm text-slate-700">
                          The sale is saved with <b>₹{(summary?.total || 0).toLocaleString('en-IN')}</b> due, and the member gets a secure link to pay it.
                          When they pay, the sale is marked paid and the receipt goes out automatically.
                        </p>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input type="checkbox" className="mt-0.5 rounded" checked={activateOnPayment} onChange={e => setActivateOnPayment(e.target.checked)} />
                          <span className="text-sm text-slate-700">
                            <b>Start the membership only after payment</b>
                            <span className="block text-[11px] text-slate-500">
                              {activateOnPayment
                                ? 'Plans, packs and credits begin when the member pays. If they pay after the start date, the plan starts that day with its full length.'
                                : 'Activate now and treat the amount as a due (like a desk sale on credit).'}
                            </span>
                          </span>
                        </label>
                        <p className="text-[11px] text-slate-500">
                          {linkCaps.canSendLinkOnWhatsApp
                            ? 'Next you’ll see the link — copy it, or send it to the member on WhatsApp.'
                            : 'Next you’ll see the link to copy and share. Sending it on WhatsApp needs the “Payment link” message enabled for your gym.'}
                        </p>
                      </section>
                    )}

                    {!byLink && (<>
                    <section className="space-y-4">
                      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-indigo-500" />
                        Select Payment Method
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {PAYMENT_METHODS.map(m => (
                          <button key={m} onClick={() => setPaymentMethod(m)}
                            className={`py-3.5 rounded-xl text-xs font-bold border-2 transition-all ${paymentMethod === m ? 'bg-indigo-600 text-white border-indigo-600 shadow-md transform -translate-y-0.5' : 'bg-white text-slate-600 border-slate-100 hover:border-slate-200'}`}>
                            {m}
                          </button>
                        ))}
                      </div>
                    </section>

                    <section className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Amount Paid (₹)</label>
                        <input type="number" value={paidNow} onChange={e => setPaidNow(e.target.value)} placeholder={`Total: ₹${summary.total}`} className="w-full p-4 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-lg" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Payment Date</label>
                        <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} className="w-full p-4 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-medium" />
                      </div>
                    </section>

                    {pendingNum > 0 && (
                      <section className="p-5 bg-orange-50 border border-orange-100 rounded-2xl space-y-4">
                        <div className="flex items-center gap-2 text-orange-700">
                           <Calendar className="w-4 h-4" />
                           <span className="text-sm font-bold">Due Date for Balance (₹{pendingNum.toLocaleString()})</span>
                        </div>
                        <input type="date" value={dueDate} min={new Date().toISOString().split('T')[0]} onChange={e => setDueDate(e.target.value)} className="w-full p-4 rounded-xl border border-orange-200 focus:border-orange-500 outline-none" />
                        <p className="text-[10px] font-medium text-orange-600/70">Remaining balance will be automatically recorded as outstanding.</p>
                      </section>
                    )}
                    </>)}

                    <section className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Notes / Remarks</label>
                      <textarea value={purchaseNotes} onChange={e => setPurchaseNotes(e.target.value)} rows={3} placeholder="Add any details about this transaction..." className="w-full p-4 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm resize-none" />
                    </section>

                    {/* Buyer GST details — only on a Tax Invoice, for members
                        purchasing on behalf of a registered business (input tax
                        credit). Optional; left blank for a normal B2C sale. */}
                    {gstContext.enabled && (
                      <section className="space-y-3">
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Buyer GST Details <span className="text-slate-300 normal-case tracking-normal font-medium">· optional</span></label>
                          <p className="text-[11px] text-slate-400 pl-1 mt-1">For a member buying on behalf of a registered business (to claim input tax credit). Leave blank for a normal sale.</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input
                            value={recipientGstin}
                            onChange={e => setRecipientGstin(e.target.value.toUpperCase())}
                            maxLength={15}
                            placeholder="Buyer GSTIN (15 characters)"
                            className="w-full p-4 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm font-mono tracking-wide uppercase"
                          />
                          <input
                            value={recipientLegalName}
                            onChange={e => setRecipientLegalName(e.target.value)}
                            placeholder="Registered business / legal name"
                            className="w-full p-4 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm"
                          />
                        </div>
                        {recipientGstin.trim() && !recipientLegalName.trim() && (
                          <p className="text-[11px] font-medium text-amber-600 pl-1">Add the buyer’s company / legal name to complete the B2B invoice.</p>
                        )}
                      </section>
                    )}
                  </div>
               </div>

               {/* summary cards for payment step */}
               <div className="w-full md:w-96 p-8 flex flex-col gap-6 shrink-0">
                  {/* Bill summary — GST breakdown for a final glance before purchase */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                     <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Bill Summary</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wide border ${gstContext.enabled ? 'bg-teal-50 text-teal-700 border-teal-100' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                           {gstContext.enabled ? 'Tax Invoice' : 'Bill of Supply'}
                        </span>
                     </div>
                     <div className="px-5 py-4 space-y-2 text-sm">
                        <div className="flex justify-between text-slate-500">
                           <span>Subtotal</span>
                           <span className="font-semibold text-slate-700 tabular-nums">₹{summary.subtotal.toLocaleString()}</span>
                        </div>
                        {summary.itemDiscountTotal > 0 && (
                           <div className="flex justify-between text-emerald-600">
                              <span>Item Discounts</span>
                              <span className="font-semibold tabular-nums">−₹{summary.itemDiscountTotal.toLocaleString()}</span>
                           </div>
                        )}
                        {summary.overallDiscountAmount > 0 && (
                           <div className="flex justify-between text-emerald-600">
                              <span>Overall Discount</span>
                              <span className="font-semibold tabular-nums">−₹{summary.overallDiscountAmount.toLocaleString()}</span>
                           </div>
                        )}
                        {gstContext.enabled ? (
                           <>
                              <div className="flex justify-between text-slate-500 pt-1 border-t border-slate-50">
                                 <span>Taxable Value</span>
                                 <span className="font-semibold text-slate-700 tabular-nums">₹{summary.taxableValue.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-slate-400 text-[13px]">
                                 <span>CGST</span>
                                 <span className="tabular-nums">₹{summary.cgst.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-slate-400 text-[13px]">
                                 <span>SGST</span>
                                 <span className="tabular-nums">₹{summary.sgst.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-slate-600 font-semibold">
                                 <span>Total GST</span>
                                 <span className="tabular-nums">₹{summary.taxTotal.toLocaleString()}</span>
                              </div>
                              {effectiveBranch?.gstin && (
                                 <p className="text-[10px] text-slate-400 pt-1">GSTIN: {effectiveBranch.gstin}</p>
                              )}
                           </>
                        ) : (
                           <div className="flex items-center gap-1.5 text-[11px] text-slate-400 pt-1 border-t border-slate-50">
                              <Info className="w-3.5 h-3.5 shrink-0" />
                              No GST charged — issued as a Bill of Supply.
                           </div>
                        )}
                     </div>
                     <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-800">Total Payable</span>
                        <span className="text-2xl font-black text-slate-900 tracking-tight tabular-nums">₹{summary.total.toLocaleString()}</span>
                     </div>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                     <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Amount Paid</span>
                        <span className="text-lg font-black text-emerald-600 text-right">₹{paidNowNum.toLocaleString()}</span>
                     </div>
                     <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Balance Due</span>
                        <span className={`text-lg font-black text-right ${pendingNum > 0 ? 'text-orange-500' : 'text-emerald-600'}`}>
                          ₹{pendingNum.toLocaleString()}
                        </span>
                     </div>
                  </div>
               </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-white flex items-center justify-between shrink-0">
          <button onClick={onClose} className="px-6 py-3 text-sm font-bold text-slate-400 hover:text-slate-600 transition-colors">
            Cancel
          </button>
          
          <div className="flex items-center gap-3">
             {step === 'items' && (
               <button onClick={() => setStep('payment')} disabled={lineItems.length === 0}
                 className="px-8 py-3 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-slate-800 disabled:opacity-50 transition-all shadow-lg hover:shadow-indigo-500/20 active:scale-95">
                 Next step: Payment
               </button>
             )}
             {step === 'payment' && (
               <button onClick={handleSubmit} disabled={submitting}
                 className="px-8 py-3 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-lg hover:shadow-emerald-500/20 active:scale-95 flex items-center gap-2">
                 {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : byLink ? <Link2 className="w-4 h-4" /> : null}
                 {byLink ? 'Save & get payment link' : 'Finalize Purchase'}
               </button>
             )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared Mini-Components

// Three staggered bouncing dots for inline "loading…" affordances.
function LoadingDots() {
  return (
    <span className="inline-flex items-end gap-0.5">
      {[0, 150, 300].map(delay => (
        <span
          key={delay}
          className="w-1 h-1 rounded-full bg-indigo-500 animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );
}

function CatalogMiniItem({ item, onAdd }: { item: CatalogItem, onAdd: () => void }) {
  return (
    <button onClick={onAdd}
      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-transparent transition-all text-left">
      <div className="flex items-center gap-3">
        <div className="shrink-0 w-8 h-8 rounded bg-white border border-slate-100 flex items-center justify-center">
           {typeIcon(item.type)}
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold text-slate-800 truncate leading-tight">{item.name}</p>
          <p className="text-[9px] font-medium text-slate-400 uppercase mt-0.5">{item.type}</p>
        </div>
      </div>
      <p className="text-xs font-bold text-slate-700 shrink-0 ml-2">₹{item.price.toLocaleString()}</p>
    </button>
  );
}

function EditableRow({ item, gstContext, minStartDate, onUpdate, onUpdateMeta, onChooseCoach, onPickSlots, onRemove }: {
  item: LineItem, gstContext: GstContext, minStartDate?: string, onUpdate: (f: keyof LineItem, v: any) => void, onUpdateMeta: (f: string, v: any) => void, onChooseCoach: (id: string) => void, onPickSlots: () => void, onRemove: () => void
}) {
  // A locked offering opens on its trainer picker — it can't be sold without one.
  const [showConfig, setShowConfig] = useState(false);
  const needsCoach = !!item.coach?.locked && !item.metadata?.trainerId;
  useEffect(() => { if (needsCoach) setShowConfig(true); }, [needsCoach]);
  // The only fields that still need a disclosure: category (custom items) and
  // membership start date. Name / GST / discount are all inline now.
  const isCustom = !item.sourceId && item.itemType !== 'membership';
  const isMembership = item.itemType === 'membership';
  const hasCoach = !!item.coach && item.coach.coaches.length > 0;
  const hasExtras = isCustom || isMembership || hasCoach;
  const extrasLabel = [isMembership && 'Start date', hasCoach && (item.metadata?.trainerName ? `Trainer: ${item.metadata.trainerName}` : 'Trainer'), item.metadata?.standing && 'Weekly times', isCustom && 'Category']
    .filter(Boolean).join(' · ');

  // Spreadsheet-style cell input: borderless, fills the cell, highlights on focus.
  const cellCls = "w-full h-8 bg-transparent font-bold text-slate-700 outline-none rounded focus:bg-white focus:ring-1 focus:ring-inset focus:ring-indigo-300 transition-all";

  const qtyField = (
    <NumberInput integer min={1} emptyValue={1} value={item.quantity} onChange={v => onUpdate('quantity', v ?? 1)}
      className={`${cellCls} text-center`} />
  );
  const gstDisabled = !gstContext.enabled; // Bill of Supply → no GST entry
  const gstField = (
    <div title={gstDisabled ? 'Bill of Supply — no GST is charged for this branch' : explainGst(item.gstPercent || 0, gstContext)}>
      <NumberInput min={0} max={100} emptyValue={0} disabled={gstDisabled} value={item.gstPercent || undefined} onChange={v => onUpdate('gstPercent', v ?? 0)} placeholder={gstDisabled ? '—' : '0'}
        className={`${cellCls} text-center ${gstDisabled ? 'opacity-40 cursor-not-allowed' : ''}`} />
    </div>
  );
  const discountField = (
    <div className="flex items-center gap-0.5 h-8">
      <NumberInput min={0} emptyValue={0} value={item.discountValue || undefined} onChange={v => onUpdate('discountValue', v ?? 0)} placeholder="0"
        className={`${cellCls} text-right px-1`} />
      <div className="flex items-center bg-slate-200/70 rounded-md p-0.5 shrink-0">
        <button onClick={() => onUpdate('discountType', 'flat')} className={`px-2 py-0.5 rounded text-[11px] font-black transition-all ${item.discountType === 'flat' ? 'bg-indigo-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>₹</button>
        <button onClick={() => onUpdate('discountType', 'percent')} className={`px-2 py-0.5 rounded text-[11px] font-black transition-all ${item.discountType === 'percent' ? 'bg-indigo-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>%</button>
      </div>
    </div>
  );
  const priceField = (
    <NumberInput min={0} emptyValue={0} value={item.price || undefined} onChange={v => onUpdate('price', v ?? 0)}
      className={`${cellCls} text-right px-1.5 text-slate-800`} />
  );

  const extras = hasExtras && showConfig && (
    <div className="flex flex-wrap gap-4">
      {isCustom && (
        <div className="w-44">
          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Category</label>
          <select value={item.itemType} onChange={e => onUpdate('itemType', e.target.value)}
            className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-white outline-none focus:border-indigo-300">
            <option value="other">Other/Custom</option>
            <option value="product">Product</option>
            <option value="service">Service</option>
          </select>
        </div>
      )}
      {hasCoach && (
        <div className="w-64">
          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">
            Trainer{item.coach!.locked ? ' · required' : ''}
          </label>
          <select value={item.metadata?.trainerId || ''} onChange={e => onChooseCoach(e.target.value)}
            className={`w-full p-2 border rounded-lg text-sm bg-white outline-none focus:border-indigo-300 ${needsCoach ? 'border-amber-400' : 'border-slate-200'}`}>
            <option value="">{item.coach!.locked ? 'Choose a trainer…' : `Any available — ₹${item.coach!.basePrice.toLocaleString('en-IN')}`}</option>
            {item.coach!.coaches.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.level ? ` · ${c.level}` : ''} — ₹{c.price.toLocaleString('en-IN')}</option>
            ))}
          </select>
          <p className="text-[10px] text-slate-500 mt-1 leading-snug">
            {item.coach!.locked ? 'Sessions are booked only with this trainer.' : 'Price follows the trainer’s level.'}
          </p>
        </div>
      )}
      {hasCoach && item.coach!.slots && item.metadata?.trainerId && (
        <div className="w-72">
          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">
            Weekly times{item.coach!.slots.scheduling === 'fixed_recurring' ? '' : ' · optional'}
          </label>
          <button onClick={onPickSlots}
            className={`w-full min-h-[40px] p-2 border rounded-lg text-sm text-left bg-white hover:border-indigo-300 ${item.metadata.standing ? 'border-slate-200 text-slate-700' : 'border-dashed border-slate-300 text-slate-500'}`}>
            {item.metadata.standing
              ? `Every ${item.metadata.standing.times.map(slotText).join(' · ')}`
              : `Choose ${item.coach!.slots.min === item.coach!.slots.max ? item.coach!.slots.min : `${item.coach!.slots.min}–${item.coach!.slots.max}`} weekly time${item.coach!.slots.max === 1 ? '' : 's'}…`}
          </button>
          <p className="text-[10px] text-slate-500 mt-1 leading-snug">
            {item.metadata.standing?.acceptClashes ? 'Clashing dates will be skipped. ' : ''}
            {item.coach!.slots.scheduling === 'fixed_recurring' && !item.metadata.standing ? 'Or set them later from the member’s Trainers tab.' : 'Checked across every week of the validity.'}
          </p>
        </div>
      )}
      {isMembership && (
        <div className="w-52">
          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Start Date</label>
          <input type="date" value={item.metadata.startDate || ''} min={minStartDate} onChange={e => onUpdateMeta('startDate', e.target.value)}
            className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-white outline-none focus:border-indigo-300" />
          {minStartDate && (
            <p className="text-[10px] text-slate-500 mt-1 leading-snug">Starts after the current plan ends (from {new Date(minStartDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}).</p>
          )}
        </div>
      )}
    </div>
  );

  const extrasToggle = hasExtras && (
    <button onClick={() => setShowConfig(!showConfig)} className="text-[10px] font-bold text-indigo-500 hover:underline flex items-center gap-1">
      {extrasLabel} <ChevronDown className={`w-3 h-3 transition-transform ${showConfig ? 'rotate-180' : ''}`} />
    </button>
  );

  return (
    <>
      {/* Desktop Row — dense, spreadsheet-style */}
      <tr className="hidden md:table-row group transition-colors hover:bg-slate-50/40 align-middle">
        <td className="py-1 px-1.5">
          <input value={item.name} onChange={e => onUpdate('name', e.target.value)} placeholder="Item name"
            className="w-full h-8 text-sm font-bold text-slate-800 bg-transparent px-1.5 rounded outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-indigo-300 transition-all" />
          {extrasToggle && <div className="px-1.5 -mt-0.5">{extrasToggle}</div>}
        </td>
        <td className="py-1 px-1 border-l border-slate-100">{qtyField}</td>
        <td className="py-1 px-1 border-l border-slate-100">{gstField}</td>
        <td className="py-1 px-1 border-l border-slate-100">{discountField}</td>
        <td className="py-1 px-1 border-l border-slate-100">{priceField}</td>
        <td className="py-1 px-3 border-l border-slate-100 text-right font-black text-slate-900 tabular-nums whitespace-nowrap">{lineTotal(item).toLocaleString()}</td>
        <td className="py-1 border-l border-slate-100 text-center">
          <button onClick={onRemove} className="p-1 text-slate-300 hover:text-red-500 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>
      {extras && (
        <tr className="hidden md:table-row bg-indigo-50/40">
          <td colSpan={7} className="px-4 py-2 border-t border-slate-100">{extras}</td>
        </tr>
      )}

      {/* Mobile Row */}
      <tr className="md:hidden">
        <td colSpan={7} className="p-3 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-2.5">
            <input value={item.name} onChange={e => onUpdate('name', e.target.value)} placeholder="Item name"
              className="flex-1 min-w-0 text-sm font-bold text-slate-800 bg-slate-50 border border-slate-100 focus:border-indigo-300 focus:bg-white rounded-lg px-2 h-9 outline-none transition-all" />
            <button onClick={onRemove} className="p-1.5 text-slate-300 hover:text-red-500 transition-colors shrink-0">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            {([['Qty', qtyField], ['Price', priceField], ['GST %', gstField], ['Discount', discountField]] as const).map(([label, field]) => (
              <div key={label} className="space-y-1">
                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block ml-1">{label}</label>
                <div className="bg-slate-50 border border-slate-100 rounded-lg px-1 focus-within:border-indigo-300 focus-within:bg-white transition-all">{field}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-2.5">
            {extrasToggle || <span />}
            <p className="text-sm font-black text-indigo-600">Total: ₹{lineTotal(item).toLocaleString()}</p>
          </div>
          {extras && <div className="mt-3">{extras}</div>}
        </td>
      </tr>
    </>
  );
}

